import { Controller } from "@hotwired/stimulus"

const BASE_TOP = 40
const BASE_PADDING_BOTTOM = 56
const LANE_GAP = 4

// Return the first available vertical lane for each horizontal rectangle.
// Inputs do not need to be sorted; output indexes match the input order.
export function nonOverlappingLanes(rectangles, gap = LANE_GAP) {
  const laneRights = []
  const assignments = Array(rectangles.length)

  rectangles
    // Read geometry explicitly instead of spreading: real DOMRects expose
    // left/right as prototype accessors, which object spread would drop.
    .map((rectangle, index) => ({ left: rectangle.left, right: rectangle.right, index }))
    .sort((a, b) => a.left - b.left)
    .forEach((rectangle) => {
      let lane = laneRights.findIndex((right) => rectangle.left >= right + gap)
      if (lane === -1) lane = laneRights.length

      laneRights[lane] = rectangle.right
      assignments[rectangle.index] = lane
    })

  return assignments
}

// Keeps benchmark labels aligned to their ticks while moving colliding labels
// onto additional rows. It re-measures after responsive width changes.
export default class extends Controller {
  static targets = ["track", "label"]

  connect() {
    this.queueLayout()

    if (typeof ResizeObserver !== "undefined" && this.hasTrackTarget) {
      this.resizeObserver = new ResizeObserver(() => this.queueLayout())
      this.resizeObserver.observe(this.trackTarget)
    }
  }

  disconnect() {
    if (this.layoutFrame) cancelAnimationFrame(this.layoutFrame)
    if (this.resizeObserver) this.resizeObserver.disconnect()
  }

  queueLayout() {
    if (this.layoutFrame) return

    this.layoutFrame = requestAnimationFrame(() => {
      this.layoutFrame = null
      this.layoutLabels()
    })
  }

  layoutLabels() {
    if (!this.hasLabelTarget) return

    const rectangles = this.labelTargets.map((label) => label.getBoundingClientRect())
    const lanes = nonOverlappingLanes(rectangles)
    const rowHeight = Math.max(...rectangles.map((rectangle) => rectangle.height), 24) + LANE_GAP

    this.labelTargets.forEach((label, index) => {
      label.style.top = `${BASE_TOP + (lanes[index] * rowHeight)}px`
    })

    const lastLane = Math.max(...lanes, 0)
    this.element.style.paddingBottom = `${BASE_PADDING_BOTTOM + (lastLane * rowHeight)}px`
  }
}